/**
 * Pipeline de modelos GLB (fase de modelos). Barril + boot.
 *
 * `preloadModels()` aquece o cache tentando cada entrada `enabled` do registry. Falhas
 * (GLB ausente) são esperadas enquanto não há assets — a entidade cai no procedural (§12).
 * SEMPRE resolve (nunca rejeita) e tem teto de tempo para não travar o boot (§14 offline).
 */
import { MODEL_REGISTRY } from "./ModelRegistry";
import { modelLoader, type EntryStatus } from "./ModelLoader";
import { modelDiagnostics, logModelDiagnostics, type EntityDiag } from "./diagnostics";

export { MODEL_REGISTRY, modelEntry, TRIANGLE_BUDGETS, weaponSocketOffset } from "./ModelRegistry";
export type { ModelEntry, AnimState, SocketName } from "./ModelRegistry";
export { modelLoader } from "./ModelLoader";
export type { EntryStatus } from "./ModelLoader";
export { createVisual, createPlayerVisual, createEnemyVisual } from "./ModelFallbackFactory";
export { attachHunterIdentity } from "./HunterIdentity";
export type { RiggedVisual, IdentityTint } from "./ModelFallbackFactory";
export { AnimationController } from "./AnimationController";
export { EquipmentAttachmentSystem } from "./EquipmentAttachmentSystem";
export { modelDiagnostics, logModelDiagnostics } from "./diagnostics";
export type { EntityDiag } from "./diagnostics";

let preloaded = false;

/** Carrega os GLBs habilitados uma vez. Resolve sempre; nunca lança. */
export async function preloadModels(): Promise<void> {
  if (preloaded) return;
  preloaded = true;
  const entries = Object.values(MODEL_REGISTRY);
  try {
    await modelLoader.preload(entries);
  } catch {
    /* preload é tolerante a falha por entrada; aqui só garante que o boot não trava */
  }
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    const w = window as unknown as { __VHmodels?: () => EntryStatus[]; __VHdiag?: () => EntityDiag[]; __VHselftest?: () => Promise<unknown> };
    w.__VHmodels = () => modelLoader.statusReport();
    w.__VHdiag = () => modelDiagnostics();
    void logStatus();
    logModelDiagnostics();
    // self-test da cadeia GLB (só DEV; não vai para produção)
    import("./pipelineSelfTest")
      .then((m) => {
        w.__VHselftest = m.runPipelineSelfTest;
        return m.runPipelineSelfTest();
      })
      .catch(() => {});
  }
}

async function logStatus(): Promise<void> {
  const rows = modelLoader.statusReport();
  const migrated = rows.filter((r) => r.status === "loaded").length;
  console.info(`[VH models] ${migrated}/${rows.length} entidade(s) com GLB carregado; demais em fallback procedural.`);
  console.table(rows.map((r) => ({ entidade: r.key, status: r.status, tris: r.triangles ?? "-", clips: r.clipNames?.length ?? "-" })));
}

/** Resumo textual do estado do pipeline (para relatório/telemetria). */
export function modelPipelineSummary(): { migrated: string[]; fallback: string[]; disabled: string[] } {
  const rows = modelLoader.statusReport();
  return {
    migrated: rows.filter((r) => r.status === "loaded").map((r) => r.key),
    fallback: rows.filter((r) => r.status === "missing" || r.status === "error").map((r) => r.key),
    disabled: rows.filter((r) => r.status === "disabled").map((r) => r.key),
  };
}
